/**
 * WebGL-based 3D Gaussian smoothing for SDF volumes.
 *
 * This module provides GPU-accelerated Gaussian smoothing using separable
 * convolution (X, Y, Z passes separately) for optimal performance.
 */

import { vec2 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { Renderer } from '../../../core/render/Renderer';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformInt } from '../../../core/utils/uniforms/UniformInt';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { PostProcessingMode } from './SDFPostProcessing';
import {
    copyTexture3D,
    createSdfTexture3D,
    readR8Texture3D,
    renderSlicePass,
} from './Texture3DUtils';
import { SDFOperations } from './SDFOperations';
import { GlOperationGuard, maybeYield, resetGlDefaults } from './utils';

/**
 * Shader for separable 1D Gaussian blur along a specified axis.
 * Uses a full-screen quad approach with 3D texture sampling.
 * Exported for reuse by the brush-bounded smoothing renderer
 * ({@link ../SmoothBrushRenderer}).
 */
export class GaussianBlurShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;

        in vec2 position;

        out vec2 vUV;

        void main() {
            // Position is in [-1, 1], convert to [0, 1] for UV
            vUV = position * 0.5 + 0.5;
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        in vec2 vUV;
        out vec4 fragColor;

        uniform sampler3D uSdfTexture;
        uniform int uAxis;           // 0 = X, 1 = Y, 2 = Z
        uniform float uSliceZ;       // Normalized Z coordinate of current slice
        uniform int uKernelRadius;   // Kernel half-width
        uniform float uKernel[32];   // Pre-computed kernel weights (max radius 15)
        uniform vec3 uTexelSize;     // 1.0 / texture dimensions

        void main() {
            vec3 texCoord = vec3(vUV, uSliceZ);
            float result = 0.0;

            // Determine step direction based on axis
            vec3 step = vec3(0.0);
            if (uAxis == 0) {
                step.x = uTexelSize.x;
            } else if (uAxis == 1) {
                step.y = uTexelSize.y;
            } else {
                step.z = uTexelSize.z;
            }

            // Apply 1D convolution along the specified axis
            for (int k = -15; k <= 15; k++) {
                if (k < -uKernelRadius || k > uKernelRadius) continue;

                vec3 sampleCoord = texCoord + step * float(k);
                // Clamp to valid texture coordinates
                sampleCoord = clamp(sampleCoord, vec3(0.0), vec3(1.0));

                float weight = uKernel[k + uKernelRadius];
                result += texture(uSdfTexture, sampleCoord).r * weight;
            }

            fragColor = vec4(result, result, result, 1.0);
        }`;

    public readonly uAxis: UniformInt;
    public readonly uSliceZ: UniformFloat;
    public readonly uKernelRadius: UniformInt;
    public readonly uTexelSizeX: UniformFloat;
    public readonly uTexelSizeY: UniformFloat;
    public readonly uTexelSizeZ: UniformFloat;

    private kernelLocation: WebGLUniformLocation | null = null;
    private texelSizeLocation: WebGLUniformLocation | null = null;

    public constructor() {
        super(
            GaussianBlurShader.VERTEX_CONTENT,
            GaussianBlurShader.FRAGMENT_CONTENT,
            'position'
        );
        this.uAxis = new UniformInt('uAxis');
        this.uSliceZ = new UniformFloat('uSliceZ');
        this.uKernelRadius = new UniformInt('uKernelRadius');
        this.uTexelSizeX = new UniformFloat('uTexelSizeX');
        this.uTexelSizeY = new UniformFloat('uTexelSizeY');
        this.uTexelSizeZ = new UniformFloat('uTexelSizeZ');

        super.storeUniformLocations(
            this.uAxis,
            this.uSliceZ,
            this.uKernelRadius
        );

        // Store kernel array location manually
        this.kernelLocation = gl.getUniformLocation(
            this.programID,
            'uKernel[0]'
        );
        this.texelSizeLocation = gl.getUniformLocation(
            this.programID,
            'uTexelSize'
        );

        // Set sampler uniform
        const samplerLocation = gl.getUniformLocation(
            this.programID,
            'uSdfTexture'
        );
        this.start();
        gl.uniform1i(samplerLocation, 0); // Use texture unit 0
        this.stop();
    }

    public loadKernel(kernel: Float32Array): void {
        if (this.kernelLocation) {
            gl.uniform1fv(this.kernelLocation, kernel);
        }
    }

    public loadTexelSize(x: number, y: number, z: number): void {
        if (this.texelSizeLocation) {
            gl.uniform3f(this.texelSizeLocation, x, y, z);
        }
    }
}

/**
 * In-plane kernel radius bound of {@link GaussianBlurShader}: its
 * `uKernel[32]` array and ±15 loop bound cap the radius at 15
 * (2 * 15 + 1 = 31 taps).
 */
export const MAX_KERNEL_RADIUS = 15;

/** Uniform array length of {@link GaussianBlurShader}'s `uKernel`. */
export const KERNEL_BUFFER_LENGTH = 32;

/**
 * Truncated, renormalized 1D Gaussian kernel — the single kernel
 * construction shared by every consumer of {@link GaussianBlurShader}
 * (the whole-structure smoothing passes below and the brush-bounded
 * ones in {@link ../SmoothBrushRenderer}), so a kernel is never built
 * two subtly different ways.
 *
 * The radius is ceil(3σ), bounded by `maxRadius`; a kernel truncated by
 * that bound is RENORMALIZED, so the weights always sum to 1 and the
 * smoothing saturates (slightly weaker than requested) instead of
 * darkening or overflowing the shader's uniform array.
 *
 * @param sigmaVoxels Gaussian sigma along this axis, in voxels
 * @param maxRadius Largest radius the calling shader can sample
 * @param bufferLength Length of the shader's kernel uniform array
 * @returns The padded kernel buffer and the radius actually used
 */
export function buildGaussianKernel(
    sigmaVoxels: number,
    maxRadius: number,
    bufferLength: number
): { kernel: Float32Array; rad: number } {
    const rad = Math.min(maxRadius, Math.max(1, Math.ceil(3 * sigmaVoxels)));
    const kernel = new Float32Array(bufferLength);
    let sum = 0;
    for (let i = -rad; i <= rad; i++) {
        const v = Math.exp(-(i * i) / (2 * sigmaVoxels * sigmaVoxels));
        kernel[i + rad] = v;
        sum += v;
    }
    for (let i = 0; i <= 2 * rad; i++) {
        kernel[i] /= sum;
    }
    return { kernel, rad };
}

/**
 * Options shared by every Gaussian-smoothing entry point below.
 *
 * Passed as an object rather than positionally: the mode, the slice
 * restriction and the abort signal are independently optional, and a
 * five-deep positional tail is exactly where a caller silently lands a
 * value in the wrong slot.
 */
export interface GaussianSmoothingOptions {
    /**
     * '3D' blurs along all axes; '2D' blurs in-plane only (X and Y),
     * leaving each native slice independent. Defaults to '3D'.
     */
    mode?: PostProcessingMode;
    /** Number of smoothing iterations. Defaults to 1. */
    iterations?: number;
    /** Optional AbortSignal for cancellation. */
    signal?: AbortSignal;
    /**
     * In '2D' mode only: when given, restrict the smoothing to this
     * single native slice (texture-grid Z index); every other slice is
     * returned byte-identical. Must be within the texture's Z range —
     * an out-of-range index is refused, never clamped. Ignored in '3D'
     * mode.
     */
    slice2DIndex?: number;
}

/**
 * {@link GaussianSmoothingOptions} plus the renderer-internal source
 * override, which only the module-level entry points below set.
 */
interface GaussianSmoothingRenderOptions extends GaussianSmoothingOptions {
    /**
     * GPU texture to blur INSTEAD of the source ContourTexture's own SDF
     * texture — must span the same voxel grid. Lets a caller blur a
     * freshly reinitialized field directly on the GPU without first
     * reading it back into a ContourTexture (see
     * {@link SDFOperations.reinitializeSDFWebGLToTexture}).
     */
    sourceTexture?: WebGLTexture;
}

/**
 * WebGL renderer for Gaussian smoothing operations on SDF textures.
 */
export class GaussianSmoothingRenderer implements Renderer {
    private shader: GaussianBlurShader;

    public constructor() {
        this.shader = new GaussianBlurShader();
    }

    /**
     * Apply Gaussian smoothing to a ContourTexture using WebGL, at an
     * explicit sigma — the physical smoothing parameter, so a caller with
     * a continuous strength control never has to round its position onto
     * the level table.
     *
     * @param src Source ContourTexture containing the SDF to smooth; its
     *        grid defines the per-axis sigma and the result is cloned
     *        from it (keeping its warm pixel cache out of the blur path)
     * @param sigma Gaussian sigma, in voxel units of the in-plane (X)
     *        axis; the per-axis sigmas below hold the physical extent
     *        constant across axes
     * @param options See {@link GaussianSmoothingOptions}
     * @returns New ContourTexture with smoothed SDF, or undefined if failed
     */
    public async applySmoothingWithSigma(
        src: ContourTexture,
        sigma: number,
        options: GaussianSmoothingRenderOptions = {}
    ): Promise<ContourTexture | undefined> {
        const {
            mode = '3D',
            iterations = 1,
            signal,
            slice2DIndex,
            sourceTexture,
        } = options;

        if (!src.signedDistanceField) {
            return undefined;
        }

        const sdf = src.signedDistanceField;
        const nx = sdf.size[0];
        const ny = sdf.size[1];
        const nz = sdf.size[2];

        if (nx === 0 || ny === 0 || nz === 0) {
            return undefined;
        }

        // A slice restriction only makes sense for in-plane smoothing,
        // and an out-of-range slice is a caller error — refuse rather
        // than silently smoothing something else.
        const singleSlice = mode === '2D' ? slice2DIndex : undefined;
        if (
            singleSlice !== undefined &&
            (!Number.isInteger(singleSlice) ||
                singleSlice < 0 ||
                singleSlice >= nz)
        ) {
            console.error(
                `applySmoothing: slice ${singleSlice} outside Z range 0..${nz - 1}`
            );
            return undefined;
        }

        let lastYield = performance.now();

        // Compute per-axis sigma in voxel units so that the physical
        // smoothing extent (in mm) is the same along every axis.
        // The base sigma is interpreted relative to the XY voxel spacing;
        // the Z sigma is scaled down when Z spacing is coarser.
        const grid = src.textureGrid;
        const sigmaMm = sigma * grid.resX;
        const sigmaX = sigmaMm / grid.resX; // = sigma (unchanged)
        const sigmaY = sigmaMm / grid.resY;
        const sigmaZ = sigmaMm / grid.resZ;

        // Per-axis 1D Gaussian kernels, built by the shared
        // constructor so the whole-structure and brush-bounded paths
        // cannot drift apart.
        const kernels = [sigmaX, sigmaY, sigmaZ].map((s) =>
            buildGaussianKernel(s, MAX_KERNEL_RADIUS, KERNEL_BUFFER_LENGTH)
        );

        // Create two temporary 3D textures for ping-pong rendering.
        // Both are released on every exit path (including abort and GL
        // errors thrown mid-pass), together with the FBO below.
        const tempTex1 = createSdfTexture3D(nx, ny, nz);
        const tempTex2 = createSdfTexture3D(nx, ny, nz);

        if (!tempTex1 || !tempTex2) {
            // Release the survivor of a partial allocation failure —
            // stranding one full-volume texture on the very failure mode
            // (VRAM pressure) that caused it would make the next attempt
            // even more likely to fail.
            if (tempTex1) {
                gl.deleteTexture(tempTex1);
            }
            if (tempTex2) {
                gl.deleteTexture(tempTex2);
            }
            console.error('Failed to create temporary textures');
            return undefined;
        }

        const texelSize = [1.0 / nx, 1.0 / ny, 1.0 / nz];

        // Release-with-acquisition: everything after the two texture
        // acquisitions that can throw — copyTexture3D's fail-fast
        // attachment checks, the FrameBufferObject constructor on an
        // exhausted GPU, and the AbortError paths of renderSlicePass /
        // readR8Texture3D / maybeYield (the previews abort on every
        // parameter change) — runs inside this guard, so no exceptional
        // path can strand the temporaries or the FBO.
        let fbo: FrameBufferObject | undefined;
        let resultPixels: Uint8Array[];
        try {
            // Copy source SDF to tempTex1
            copyTexture3D(sourceTexture ?? sdf.texture, tempTex1, nx, ny, nz);

            // Create FBO for rendering
            fbo = new FrameBufferObject(vec2.fromValues(nx, ny));
            let srcTex = tempTex1;
            let dstTex = tempTex2;

            // In 2D mode the Z pass is skipped entirely so no values
            // ever cross slice boundaries.
            const axes = mode === '2D' ? [0, 1] : [0, 1, 2];

            // Single-slice 2D: only the target slice is re-rendered.
            // The in-plane passes never sample other slices, and after
            // an even number of ping-pong passes (2D always runs two per
            // iteration) the result lands back on tempTex1 — the full
            // copy of the source — so every other slice is
            // byte-identical by construction.
            const zMin = singleSlice ?? 0;
            const zMax = singleSlice ?? nz - 1;

            for (let it = 0; it < iterations; it++) {
                for (const axis of axes) {
                    this.shader.start();
                    this.shader.loadTexelSize(
                        texelSize[0],
                        texelSize[1],
                        texelSize[2]
                    );
                    gl.viewport(0, 0, nx, ny);
                    RenderStorage.getQuadVAO().bind(0);

                    this.shader.uAxis.loadInt(axis);
                    this.shader.uKernelRadius.loadInt(kernels[axis].rad);
                    this.shader.loadKernel(kernels[axis].kernel);

                    renderSlicePass(
                        srcTex,
                        dstTex,
                        fbo,
                        this.shader.uSliceZ,
                        nz,
                        signal,
                        zMin,
                        zMax
                    );
                    [srcTex, dstTex] = [dstTex, srcTex];

                    RenderStorage.getQuadVAO().unbind(0);
                    this.shader.stop();
                    fbo.unbind();
                    resetGlDefaults();

                    lastYield = await maybeYield(lastYield, signal);
                }
            }

            // Read result back from srcTex (last destination)
            resultPixels = readR8Texture3D(srcTex, nx, ny, nz, signal);
        } finally {
            fbo?.delete();
            gl.deleteTexture(tempTex1);
            gl.deleteTexture(tempTex2);
            resetGlDefaults();
        }

        // `resultPixels` is a freshly allocated local readback buffer, so
        // the clone can adopt it as its cache rather than re-reading the
        // volume on first access.
        return src.cloneWithNewSdf(resultPixels, { adoptPixels: true });
    }

    public delete(): void {
        this.shader.delete();
    }
}

/**
 * Apply WebGL-accelerated Gaussian smoothing to a ContourTexture, at an
 * explicit Gaussian sigma. Sigma — not a stepped level — is the module's
 * only strength parameter: every consumer's control is continuous and
 * resolves its position through {@link getSigmaForStrength} (the
 * structure smoothing panel, threshold segmentation), so a stepped entry
 * point would only add a rounding step back onto the level table.
 *
 * In '3D' mode the source SDF is first reinitialized via
 * {@link SDFOperations.reinitializeSDFWebGLToTexture} to ensure it is a
 * proper metric distance field (especially along the Z axis for
 * structures with contours on only a few slices). This prevents
 * aggressive smoothing artifacts from non-metric distance values. The
 * reinitialized field is handed to the blur as a GPU texture — only the
 * final smoothed volume is ever read back to the CPU.
 *
 * In '2D' mode the blur never samples along Z, and each slice of the
 * display SDF is already a metric 2D field within the falloff band (see
 * docs/sdf-model.md), so the reinitialization is skipped. That holds
 * only while the in-plane kernel stays inside the band: the smoothing
 * panel, the sole 2D caller, tops out at strength 5 (sigma 1.4, radius
 * ceil(3 * 1.4) = 5 voxels), which the band covers.
 *
 * @param src Source ContourTexture
 * @param sigma Gaussian sigma, in voxel units of the in-plane (X) axis
 * @param options See {@link GaussianSmoothingOptions}
 * @returns New smoothed ContourTexture, or undefined if failed
 */
export async function applyGaussianSmoothingSigmaGL(
    src: ContourTexture,
    sigma: number,
    options: GaussianSmoothingOptions = {}
): Promise<ContourTexture | undefined> {
    if (!GlOperationGuard.acquire('Gaussian smoothing')) {
        return undefined;
    }
    try {
        const renderer = RenderStorage.getRenderer(GaussianSmoothingRenderer);

        if (options.mode === '2D') {
            return await renderer.applySmoothingWithSigma(src, sigma, options);
        }

        // Reinitialize SDF to a proper metric distance field before
        // smoothing. This propagates distance values in all directions
        // (especially Z) and prevents aggressive smoothing on
        // structures with contours on only a few slices. The reinit
        // result stays on the GPU: the blur reads it via the
        // sourceTexture override, so nothing round-trips through the CPU
        // until the final smoothed readback (previously the reinit was
        // read back and re-uploaded — two extra full-volume transfers
        // per preview).
        const reinitTexture =
            await SDFOperations.reinitializeSDFWebGLToTexture(src);
        if (!reinitTexture) {
            return undefined;
        }

        try {
            // `src` (not the reinit target) stays the clone source so the
            // result inherits its metadata and warm pixel cache path.
            return await renderer.applySmoothingWithSigma(src, sigma, {
                ...options,
                sourceTexture: reinitTexture.signedDistanceField?.texture,
            });
        } finally {
            // Also on AbortError from applySmoothing — the reinit target
            // is this function's own acquisition.
            reinitTexture.delete();
        }
    } finally {
        GlOperationGuard.release();
    }
}
